import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { compose, prop, pick, assoc, over, lensProp } from 'ramda';
import { toast } from 'react-toastify';
import moment from 'moment';

import { Formik, Form } from 'formik';
import { Typography, Grid, Chip, Collapse, Switch, FormControlLabel } from '@material-ui/core';
import ModalDrawerSwitcher from '../ModalDrawerSwitcher';
import Modal from '../Modal';
import Loader from '../Loader';
import Select from '../Select';
import Input from '../Input';
import Button from '../Button';
import ExportPDF from '../ExportPDF';
import SingleDatePicker from '../SingleDatePicker';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload } from '@fortawesome/free-solid-svg-icons';

import { sendNfse } from 'pages/ProvisionalReceiptConfig/store/thunk';
import { loadNfseConcept } from 'pages/ProvisionalReceipt/store/thunk';
import { setRpsInfo } from 'pages/ProvisionalReceipt/store/actions';
import { loadAllPaymentMethods } from 'pages/PaymentMethods/store/thunk';
import { closeInvoice } from '../../pages/ClientList/store/services';
import { requestLoadClients } from 'pages/ClientList/store/thunk';
import { handlePaying } from '../../pages/ClientInvoices/store/actions';
import { fetchClientInvoiceServiceOrders } from '../../pages/ClientInvoices/store/services';
import { loadClientInvoices, loadInvoiceDetails, loadReceiptInvoicePrint, upsertInvoice, payInvoice, undoPayment, emailInvoice } from '../../pages/ClientInvoices/store/thunk';

import useWindowDimensions from 'helpers/hooks/useWindowDimensions';
import { convertAmountToDB, convertInvoicePrint, convertToEmptyString, convertDateToDB, convertDateAndTimeToDB, convertFormattedMoneyToNumber, convertToBrl } from '../../helpers/converters';

import { validations } from './validations';

import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles({
  textInfo: {
    margin: 0,
    marginTop: 10,
    color: '#f44336'
  }
});

export default function InvoicePos({ isOpen, setIsOpenInvoicePos, establishmentId, pageOrigin, clientsPage, transactionsQp, transactionsPage, transactionsPageSize }) {
  const dispatch = useDispatch();

  const classes = useStyles();

  const { width } = useWindowDimensions();
  const isSm = width < 960;

  const [isOpenConfirmed, setIsOpenConfirmed] = useState(false);
  const [isClientPaying, setIsClientPaying] = useState(false);
  const [isCancelingPayment, setIsCancelingPayment] = useState(false);
  const [toggleRecebido, setToggleRecebido] = useState(false);
  const [closingDateTimeLastDate, setClosingDateTimeLastDate] = useState(null);

  const { userId } = useSelector(state => state.profile);
  const { establishmentId: clientListEstablishmentId } = useSelector(state => state.businessInfo);
  const { clientId } = useSelector(state => state.clientRegistration.basicInfo);
  const { activePage: page, size: pageSize } = useSelector(state => state.pagination);
  const { qp } = useSelector(state => state.filters);
  const { startDate, endDate } = useSelector(state => state.dateFilter);
  const { clientName } = useSelector(state => state.clientInvoices.submit);
  const { penalty, penaltyType } = useSelector(state => state.clientRegistration.charges.submit);
  const { isSendNfseLoading } = useSelector(state => state.provisionalReceiptConfig);
  const situationEstablishments = useSelector(state => state.login.situation.establishments);
  const { option } = useSelector(state => state.modalDrawerSwitcher);

  const isModal = option == 'modal';

  const [availablePaymentMethods, setAvailablePaymentMethods] = useState([
    {
      label: '--',
      value: ''
    }
  ]);

  const {
    isDetailing,
    isPaying,
    isEmailing,
    isCanceling,
    invoiceDateTime,
    submit,
    submit: {
      situationId,
      invoiceSituation,
      opened,
      paymentDateTime,
      closingDateTime,
      closingUserName,
      dueDateTime,
      userOutputName,
      transactionId
    }
  } = useSelector(state => state.clientInvoices);

  const {
    isNfInfo,
    nfInfo: {
      rpsSeries,
      rpsNumber,
      nfNumber,
      nfStatus
    } = {}
  } = useSelector(state => state.provisionalReceipt);

  const taxSettings = situationEstablishments
    ?.find(establishment => +establishment?.establishmentId == +submit?.establishmentId)
    ?.nfseTaxSetting;

  const handlePayment = (
    {
      amount,
      amountReceived,
      paymentMethod,
      paymentDateTime,
      dueDateTime,
      closingDateTime,
      currentAmount,
      isUpdate
    },
    { resetForm }
  ) => {
    const convertDateToDB = date => `${date} 00:00:01`;

    const handleAmountToSend = amount =>
      amountReceived ? convertAmountToDB(amountReceived) : convertAmountToDB(amount);

    const params = toggleRecebido
      ? {
        amount: handleAmountToSend(handleAmountToSend(amount)),
        paymentDateTime,
        paymentMethod
      }
      : {
        situationId,
        amount: convertAmountToDB(currentAmount),
        dueDateTime: convertDateToDB(dueDateTime)
      }

    const properties = {
      userId,
      establishmentId,
      clientListEstablishmentId,
      clientId,
      invoiceDateTime,
      page: pageOrigin == 'clients' ? clientsPage : page,
      pageSize,
      startDate,
      endDate,
      pageOrigin,
      qp
    }

    if(isUpdate) {
      const updateParams = {
        closingDateTime: moment(closingDateTime).creationData().format == 'YYYY-MM-DD' ? `${closingDateTime} 00:00:00` : moment(closingDateTime, 'DD/MM/YYYY').format('YYYY-MM-DD HH:mm:ss'),
        dueDateTime: convertDateToDB(dueDateTime),
        amount: convertAmountToDB(amount)
      }

      return dispatch(upsertInvoice(updateParams, properties)).then(error => {
        if(!error) {
          resetForm();
          setIsOpenInvoicePos(false);
        }
      });
    }

    if(toggleRecebido) {
      dispatch(payInvoice(params, properties));
      setIsOpenInvoicePos(false);
    } else {
      dispatch(upsertInvoice(params, properties)).then(error => {
        if(!error) {
          resetForm();
        }
      });
    }
  }

  const updateDueDateTime = (dueDateTime) => {
    const properties = {
      userId,
      establishmentId,
      clientId,
      invoiceDateTime,
      page,
      pageSize,
      startDate,
      endDate,
      pageOrigin,
      qp
    }

    const updateParams = {
      dueDateTime
    }

    return dispatch(upsertInvoice(updateParams, properties)).then(error => {
      if(!error) {
        setIsOpenInvoicePos(false);
      }
    });
  }

  useEffect(() => {
    if(situationId === 2 || opened === 0) {
      setToggleRecebido(true);
    } else {
      setToggleRecebido(false);
    }
  }, [situationId, opened]);

  useEffect(() => {
    dispatch(setRpsInfo({
      rpsSeries: '',
      rpsNumber: '',
      nfNumber: '',
      nfStatus: ''
    }));

    const { clientId, invoiceDateTime } = submit || {};
    const id = `${clientId}${invoiceDateTime}`;

    if(+taxSettings == 1 && !!transactionId && (+situationId == 2 || +situationId == 3)) {
      if(id) {
        dispatch(loadNfseConcept(userId, establishmentId, id));
      }
    }
  }, [taxSettings, transactionId, situationId]);

  const handleSendInvoice = ({ email }) => {
    dispatch(emailInvoice(userId, establishmentId, clientId, invoiceDateTime, { email }));
  }

  const handleSubmit = (values, actions) => {
    if(!moment(values.paymentDateTime).isValid()) {
      return toast.error('Selecione uma data de pagamento válida.');
    }

    if(isCancelingPayment) {
      return handleCancelInvoice(values, actions);
    }

    if(isEmailing) {
      return dispatch(handleSendInvoice(values, actions));
    }

    const paymentDateTime =
      values.paymentDateTime == '0001-01-01' ||
      values.paymentDateTime == '0001-01-01 00:00:00' ||
      values.paymentDateTime == 'Data inválida' ||
      values.paymentDateTime == 'Invalid date' ||
      moment().isSame(values.paymentDateTime, 'day')
        ? moment().format('YYYY-MM-DD HH:mm:ss')
        : `${values.paymentDateTime} ${moment().format('HH:mm:ss')}`;

    const params = {
      ...values,
      paymentDateTime: convertDateAndTimeToDB(paymentDateTime),
      isUpdate: opened == 1
    }

    return dispatch(handlePayment(params, actions));
  }

  const handleCancelInvoice = ({ reason, userPassword }, { resetForm }) => {
    const params = {
      situationId: 1,
      reason,
      userPassword
    }

    const properties = {
      userId,
      establishmentId,
      clientId,
      invoiceDateTime: invoiceDateTime?.toString()?.slice(-14),
      page: pageOrigin == 'transactions' ? transactionsPage : page,
      pageSize: pageOrigin == 'transactions' ? transactionsPageSize : pageSize,
      startDate,
      endDate,
      pageOrigin,
      qp: pageOrigin == 'transactions' ? transactionsQp : qp
    }

    return dispatch(undoPayment(params, properties)).then(error => {
      if(!error) {
        setIsCancelingPayment(false);
        resetForm();
      }
    });
  }

  const handleLoadInvoiceDetails = () => {
    if(pageOrigin == 'transactions') {
      return dispatch(loadInvoiceDetails(userId, establishmentId, clientId, invoiceDateTime?.toString()?.slice(-14)));
    }

    return dispatch(loadInvoiceDetails(userId, establishmentId, clientId, invoiceDateTime));
  }

  const handlePrintInvoice = () => {
    const params = {
      userId,
      establishmentId,
      clientId,
      invoiceDateTime: invoiceDateTime?.toString()?.slice(-14)
    }

    return dispatch(loadReceiptInvoicePrint(params));
  }

  const handleLoadReceiptServiceOrderInvoicePos = () => {
    const params = {
      userId,
      establishmentId,
      clientId,
      invoiceDateTime: invoiceDateTime?.toString()?.slice(-14)
    }

    return fetchClientInvoiceServiceOrders(params);
  }

  const togglePaymentOptions = value => {
    if(!value) {
      setIsOpenConfirmed(true);
    } else {
      dispatch(handlePaying());
    }
  }

  useEffect(() => {
    if(opened == 1) {
      togglePaymentOptions(true);
    }

    handleLoadInvoiceDetails();

    dispatch(loadAllPaymentMethods(userId, establishmentId)).then(data => {
      const paymentMethods = data?.map(item => {
        return {
          label: item?.paymentMethodName,
          value: item?.establishmentPaymentMethodId
        }
      });

      setAvailablePaymentMethods([
        ...availablePaymentMethods,
        ...paymentMethods
      ]);
    });
  }, []);

  const handleSaveButton = () => {
    if(toggleRecebido) {
      return 'Pagar fatura';
    }

    return 'Salvar';
  }

  const closeInvoiceFunction = (clientId, invoiceDateTime, values) => {
    const { closingDateTime, dueDateTime, amount } = values || {};

    const updateParams = {
      closingDateTime: moment(closingDateTime).creationData().format == 'YYYY-MM-DD' ? `${closingDateTime} 00:00:00` : moment(closingDateTime, 'DD/MM/YYYY').format('YYYY-MM-DD HH:mm:ss'),
      dueDateTime: moment(dueDateTime, 'YYYY-MM-DD').format('YYYY-MM-DD HH:mm:ss'),
      amount: convertAmountToDB(amount)
    }

    const properties = {
      userId,
      establishmentId,
      clientId,
      invoiceDateTime,
      page,
      pageSize,
      startDate,
      endDate,
      pageOrigin,
      qp,
      isClosing: true
    }

    setIsOpenConfirmed(false);

    dispatch(upsertInvoice(updateParams, properties)).then(error => {
      if(!error) {
        closeInvoice(userId, establishmentId, clientId, invoiceDateTime).then(() => {
          if(pageOrigin == 'invoices') {
            dispatch(loadClientInvoices(userId, establishmentId, clientId));
          }

          if(pageOrigin == 'clients') {
            dispatch(
              requestLoadClients({
                userId,
                establishmentId: clientListEstablishmentId,
                page: clientsPage,
                pageSize,
                qp
              })
            );
          }

          handleLoadInvoiceDetails();
        });
      }
    });
  }

  const getClosingDateTime = (dates) => {
    const validDate = dates?.filter(date => !!date)[0];

    if(validDate) {
      return moment(validDate, 'DD/MM/YYYY').format('YYYY-MM-DD');
    }

    return moment().format('YYYY-MM-DD');
  }

  const getChipStyle = () => {
    if(invoiceSituation === 'Cancelada') {
      return {
        color: '#F44336',
        backgroundColor: 'rgba(244, 67, 54, 0.2)'
      }
    }

    if(invoiceSituation === 'Paga') {
      return {
        color: '#4CAF50',
        backgroundColor: 'rgba(76, 175, 80, 0.2)'
      }
    }

    return {
      color: '#FF9800',
      backgroundColor: 'rgba(255, 152, 0, 0.2)'
    }
  }

  const labelStatus = () => {
    return(
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <Typography variant='body1'>
          Detalhamento da fatura
        </Typography>
        <Chip
          label={invoiceSituation}
          style={getChipStyle()}
        />
      </div>
    );
  }

  const initialValues = compose(
    over(lensProp('paymentMethod'), convertToEmptyString),
    assoc('opened', opened),
    assoc('isCanceling', isCanceling),
    assoc('isCancelingPayment', isCancelingPayment),
    assoc('isEmailing', isEmailing),
    assoc('isPaying', isPaying),
    pick([
      'dueDateTime',
      'closingDateTime',
      'currentDueDateTime',
      'paymentMethod',
      'paymentDateTime',
      'amount',
      'closingAmount',
      'amountReceived',
      'currentAmount',
      'email',
      'reason',
      'userPassword',
      'clientName'
    ]),
    prop('submit')
  );

  const invoicePaid = invoiceSituation === 'Paga' || invoiceSituation === 'Cancelada';
  const isInvoiceClosed = invoiceSituation === 'Paga' || isClientPaying;
  const isInvoiceOpened = opened === 1;
  const isInvoiceClosedAndOpened = opened != 1 && invoiceSituation != 'Paga';

  return(
    <>
      <ModalDrawerSwitcher
        id="accounts"
        title={labelStatus()}
        open={isOpen}
        onClose={() => setIsOpenInvoicePos(false)}
      >
        <Formik
          enableReinitialize
          initialValues={initialValues(useSelector(state => state.clientInvoices))}
          validationSchema={validations}
          onSubmit={handleSubmit}
        >
          {({ ...formikProps }) => {
            return(
              <>
                <Modal
                  title="Tem certeza que deseja fechar esta fatura ?"
                  scroll="body"
                  maxWidth="md"
                  open={isOpenConfirmed}
                  onClose={() => setIsOpenConfirmed(false)}
                >
                  <p>Esta ação não pode ser desfeita.</p>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                    <Button
                      color="error"
                      variant="outlined"
                      onClick={() => setIsOpenConfirmed(false)}
                    >
                      Cancelar
                    </Button>
                    <Button
                      color="success"
                      className="mr-2"
                      onClick={() => closeInvoiceFunction(clientId, invoiceDateTime, formikProps.values)}
                    >
                      Confirmar
                    </Button>
                  </div>
                </Modal>
                <Form style={{ height: '100%' }}>
                  <Loader isLoading={isDetailing}>
                    <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%' }}>
                      <div>
                        <Grid container spacing={2}>
                          <Grid item xs={(isModal && !isSm) ? 6 : 12}>
                            <Input
                              disabled
                              id="clientName"
                              name="clientName"
                              label="Cliente"
                              value={clientName || formikProps.values.clientName}
                            />
                          </Grid>
                          <Grid item xs={(isModal && !isSm) ? 3 : 12}>
                            <SingleDatePicker
                              name="closingDateTime"
                              label="Data de fechamento"
                              disabled={invoicePaid || isInvoiceClosedAndOpened}
                              initialDate={() => getClosingDateTime([closingDateTimeLastDate, closingDateTime, formikProps.values.closingDateTime])}
                              blockPastDates
                              onBlockedDate={() => toast.error('A data de fechamento da fatura não pode ser menor ou igual a data de hoje.')}
                              onDateChange={date => {
                                formikProps.setFieldValue('closingDateTime', moment(date, 'DD/MM/YYYY').format('YYYY-MM-DD'));
                                setClosingDateTimeLastDate(moment(date, 'DD/MM/YYYY').format('YYYY-MM-DD'));
                              }}
                            />
                          </Grid>
                          <Grid item xs={(isModal && !isSm) ? 3 : 12}>
                            <SingleDatePicker
                              name="dueDateTime"
                              label="Data de vencimento"
                              disabled={isInvoiceClosed || invoicePaid || (+opened != 1)}
                              initialDate={dueDateTime || formikProps.values.dueDateTime}
                              blockPastDates={opened == 1}
                              onBlockedDate={() => toast.error('A data de vencimento da fatura não pode ser menor do que a data de hoje.')}
                              onDateChange={date => formikProps.setFieldValue('dueDateTime', convertDateToDB(date))}
                            />
                          </Grid>
                        </Grid>
                        <Grid container spacing={2}>
                          <Grid item md={isModal ? 3 : 6} xs={12}>
                            <Input
                              disabled
                              id="closingAmount"
                              name="closingAmount"
                              label="Valor acumulado"
                              type="money"
                              value={convertToBrl(convertFormattedMoneyToNumber(formikProps.values.closingAmount))}
                            />
                          </Grid>
                          <Grid item md={isModal ? 3 : 6} xs={12}>
                            <Input
                              disabled={opened != 1}
                              id="amount"
                              name="amount"
                              label="Valor fixo"
                              type="money"
                              value={convertToBrl(convertFormattedMoneyToNumber(formikProps.values.amount))}
                              onChange={formikProps.handleChange}
                            />
                          </Grid>
                          <Grid item md={isModal ? 3 : 6} xs={12}>
                            <Input
                              disabled
                              id="penaltyType"
                              name="penaltyType"
                              label={penaltyType === 1 ? 'Multa (R$)' : 'Multa (%)'}
                              value={penalty}
                            />
                          </Grid>
                          <Grid item md={isModal ? 3 : 6} xs={12}>
                            <Input
                              disabled
                              id="currentAmount"
                              name="currentAmount"
                              label="Valor"
                              type="money"
                              value={convertToBrl(convertFormattedMoneyToNumber(formikProps.values.amount) + convertFormattedMoneyToNumber(formikProps.values.closingAmount))}
                            />
                          </Grid>
                        </Grid>
                        <hr />
                        <Grid container spacing={2}>
                          {+opened == 1 ? (
                            <>
                              <Grid item xs={12}>
                                <FormControlLabel
                                  id="invoiceSituation"
                                  name="invoiceSituation"
                                  label="Fechar fatura"
                                  control={
                                    <Switch
                                      color="primary"
                                      checked={formikProps.values.isPaying ||invoiceSituation === 'Paga'}
                                      disabled={
                                        formikProps.values.currentDueDateTime !==
                                        formikProps.values.dueDateTime ||
                                        invoicePaid
                                      }
                                      onChange={() => {
                                        togglePaymentOptions(formikProps.values.isPaying);
                                        setIsClientPaying(formikProps.values.isPaying);
                                      }}
                                    />
                                  }
                                />
                              </Grid>
                            </>
                          ) : (
                            <>
                              <Grid item xs={(isModal && !isSm) ? 3 : 12}>
                                <FormControlLabel
                                  disabled
                                  id="invoiceSituation"
                                  name="invoiceSituation"
                                  label="Fatura Fechada"
                                  control={<Switch color="primary" checked />}
                                />
                              </Grid>
                              <Grid
                                item
                                xs={(isModal && !isSm) ? 9 : 12}
                                style={{ display: 'flex', flexDirection: 'column', marginBottom: 10 }}
                              >
                                {situationId === 2 && (
                                  <p style={{ margin: 0 }}>Recebido por {userOutputName} ás {moment(paymentDateTime).format('DD/MM/YYYY HH:mm')}</p>
                                )}
                                {(!!closingUserName && !!closingDateTime) && (
                                  <p style={{ margin: 0 }}>Fechada por {closingUserName} no dia {closingDateTime?.split(' ')[0]} ás {closingDateTime?.split(' ')[1]}</p>
                                )}
                                {(invoiceSituation === 'Paga' && rpsNumber > 0) && (
                                  <p style={{ margin: 0 }}>RPS: {rpsSeries}-{rpsNumber} | N˚ NFSe: {nfNumber}</p>
                                )}
                              </Grid>
                            </>
                          )}
                          {opened === 1 && (
                            <Grid item xs={12} styles={{ marginLeft: -50 }}>
                              <p className={classes.textInfo}>
                                <strong>Atenção</strong>: Fechamento da fatura <u>não</u> pode ser desfeito.
                              </p>
                            </Grid>
                          )}
                        </Grid>
                        <Collapse in={toggleRecebido}>
                          <Grid container spacing={2}>
                            <Grid item md={isModal ? 3 : 12} xs={12}>
                              <SingleDatePicker
                                name="paymentDateTime"
                                label="Data do recebimento"
                                disabled={invoicePaid}
                                initialDate={
                                  moment(paymentDateTime).format('DD/MM/YYYY') == '01/01/0001'
                                    ? moment().format('YYYY-MM-DD')
                                    : paymentDateTime
                                }
                                onDateChange={date => formikProps.setFieldValue('paymentDateTime', convertDateToDB(date))}
                              />
                            </Grid>
                            <Grid item md={isModal ? 6 : 12} xs={12}>
                              <Select
                                id="paymentMethod"
                                name="paymentMethod"
                                label="Selecione o meio de pagamento"
                                disabled={invoicePaid}
                                options={availablePaymentMethods}
                                error={formikProps.errors.paymentMethod}
                                helperText={formikProps.errors.paymentMethod}
                                value={formikProps.values.paymentMethod}
                                onChange={formikProps.handleChange}
                              />
                            </Grid>
                            <Grid item md={isModal ? 3 : 12} xs={12}>
                              <Input
                                id="amountReceived"
                                name="amountReceived"
                                type="money"
                                label="Valor recebido"
                                placeholder="R$ 0,00"
                                disabled={invoicePaid}
                                value={formikProps.values.amountReceived}
                                error={formikProps.errors.amountReceived}
                                helperText={formikProps.errors.amountReceived}
                                onChange={formikProps.handleChange}
                              />
                            </Grid>
                          </Grid>
                        </Collapse>
                        <Grid container spacing={1} alignItems='center' style={{ marginTop: 10 }}>
                          {isSm && (
                            <Grid item xs={4}>
                              <ExportPDF
                                fullWidth
                                fontSize={8}
                                type="button"
                                exportType="receiptInvoice"
                                color="tertiary"
                                variant="outlined"
                                fileName="fatura"
                                disabled={!situationId}
                                loading={isDetailing}
                                formatComponentData={convertInvoicePrint}
                                requestData={handlePrintInvoice}
                                startIcon={
                                  <FontAwesomeIcon
                                    size='xs'
                                    icon={faDownload}
                                  />
                                }
                              >
                                Baixar fatura
                              </ExportPDF>
                            </Grid>
                          )}
                          {isSm && (
                            <Grid item xs={4}>
                              <ExportPDF
                                fullWidth
                                fontSize={8}
                                type="button"
                                exportType="receiptOsInvoice"
                                color="tertiary"
                                variant="outlined"
                                fileName="fatura_os"
                                requestData={handleLoadReceiptServiceOrderInvoicePos}
                                startIcon={
                                  <FontAwesomeIcon
                                    size='xs'
                                    icon={faDownload}
                                  />
                                }
                              >
                                Extrato
                              </ExportPDF>
                            </Grid>
                          )}
                          {isSm && (
                            <Grid item xs={4}>
                              {((+taxSettings == 1) && !!transactionId) && (
                                <>
                                  {(!isNfInfo && nfStatus != 2) && (
                                    <Button
                                      fullWidth
                                      fontSize={8}
                                      color="success"
                                      type="button"
                                      loading={isSendNfseLoading}
                                      onClick={() => {
                                        const { establishmentId, clientId, invoiceDateTime } = submit || {};
                                        dispatch(sendNfse(userId, establishmentId, `${clientId}${invoiceDateTime}`, { resend: 1 }));
                                      }}
                                    >
                                      Enviar NFS-e
                                    </Button>
                                  )}
                                </>
                              )}
                            </Grid>
                          )}
                        </Grid>
                      </div>
                      <div style={{ marginBottom: 5 }}>
                        <Grid container spacing={1} justify='flex-end' alignItems='center'>
                          <Grid item xs={12}>
                            <Collapse in={isCancelingPayment}>
                              <Grid container spacing={2}>
                                <Grid item md={6} xs={12}>
                                  <Input
                                    id="reason"
                                    label="Motivo do cancelamento"
                                    error={formikProps.errors.reason}
                                    helperText={formikProps.errors.reason}
                                    onChange={formikProps.handleChange}
                                  />
                                </Grid>
                                <Grid item md={6} xs={12}>
                                  <Input
                                    id="userPassword"
                                    type="password"
                                    label="Confirme sua senha"
                                    error={formikProps.errors.userPassword}
                                    helperText={formikProps.errors.userPassword}
                                    onChange={formikProps.handleChange}
                                  />
                                </Grid>
                              </Grid>
                            </Collapse>
                          </Grid>
                          {!isSm && (
                            <Grid item>
                              <ExportPDF
                                type="button"
                                exportType="receiptInvoice"
                                color="tertiary"
                                variant="outlined"
                                fileName="fatura"
                                disabled={!situationId}
                                loading={isDetailing}
                                formatComponentData={convertInvoicePrint}
                                requestData={handlePrintInvoice}
                                startIcon={
                                  <FontAwesomeIcon
                                    size='xs'
                                    icon={faDownload}
                                  />
                                }
                              >
                                Baixar fatura
                              </ExportPDF>
                            </Grid>
                          )}
                          {!isSm && (
                            <Grid item>
                              <ExportPDF
                                type="button"
                                exportType="receiptOsInvoice"
                                color="tertiary"
                                variant="outlined"
                                fileName="fatura_os"
                                requestData={handleLoadReceiptServiceOrderInvoicePos}
                                startIcon={
                                  <FontAwesomeIcon
                                    size='xs'
                                    icon={faDownload}
                                  />
                                }
                              >
                                Extrato
                              </ExportPDF>
                            </Grid>
                          )}
                          {(invoiceSituation == 'Paga' && !isCancelingPayment) && (
                            <Grid item xs={6} sm={3}>
                              <Button
                                fullWidth
                                fontSize={isSm ? 8 : ''}
                                type="button"
                                color="error"
                                variant="outlined"
                                onClick={() => setIsCancelingPayment(true)}
                              >
                                Desfazer pagamento
                              </Button>
                            </Grid>
                          )}
                          {isCancelingPayment && (
                            <Grid item style={{ display: 'flex', gap: 10 }}>
                              <Button
                                type="button"
                                color="error"
                                variant="outlined"
                                onClick={() => setIsCancelingPayment(false)}
                              >
                                Cancelar
                              </Button>
                              <Button
                                type="submit"
                                color="primary"
                              >
                                Confirmar
                              </Button>
                            </Grid>
                          )}
                          {(situationId !== 2 || invoiceSituation != 'Paga') && (
                            <>
                              {(!isInvoiceOpened && invoiceSituation != 'Atrasada') && (
                                <Grid item xs={6} sm={3}>
                                  <Button
                                    fullWidth
                                    fontSize={isSm ? 8 : ''}
                                    type="button"
                                    color="primary"
                                    onClick={() => updateDueDateTime(formikProps.values.dueDateTime)}
                                  >
                                    Salvar
                                  </Button>
                                </Grid>
                              )}
                              <Grid item xs={6} sm={3}>
                                <Button
                                  fullWidth
                                  fontSize={isSm ? 8 : ''}
                                  type={invoiceSituation === 'Paga' ? 'button' : 'submit'}
                                  color="success"
                                  loading={isDetailing}
                                  disabled={invoiceSituation === 'Cancelada'}
                                  onClick={null}
                                >
                                  {handleSaveButton()}
                                </Button>
                              </Grid>
                            </>
                          )}
                          {!isSm && (
                            <>
                              {((+taxSettings == 1) && !!transactionId) && (
                                <>
                                  {(!isNfInfo && nfStatus != 2) && (
                                    <Button
                                      color="success"
                                      type="button"
                                      loading={isSendNfseLoading}
                                      onClick={() => {
                                        const { establishmentId, clientId, invoiceDateTime } = submit || {};
                                        dispatch(sendNfse(userId, establishmentId, `${clientId}${invoiceDateTime}`, { resend: 1 }));
                                      }}
                                    >
                                      Enviar NFS-e
                                    </Button>
                                  )}
                                </>
                              )}
                            </>
                          )}
                        </Grid>
                      </div>
                    </div>
                  </Loader>
                </Form>
              </>
            );
          }}
        </Formik>
      </ModalDrawerSwitcher>
    </>
  );
}

InvoicePos.propTypes = {
  pageOrigin: PropTypes.string,
  setIsOpenInvoicePos: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired
};

InvoicePos.defaultProps = {
  pageOrigin: 'invoices'
};